import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { startRegistration } from '@simplewebauthn/browser';
import { useAuth } from '../../context/AuthContext';
import { getDashboardPath } from '../../config/roleConfig';
import { Logo } from '../common';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1';

const STEPS = {
  CHOOSE: 'choose',
  SETUP: 'setup',
  RECOVERY: 'recovery',
  DONE: 'done',
};

const MfaSetupPage = () => {
  const navigate = useNavigate();
  const { user, authToken, clearMfaSetupRequired } = useAuth();

  const [step, setStep] = useState(STEPS.CHOOSE);
  const [method, setMethod] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // TOTP state
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [totpCode, setTotpCode] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  };

  // ── Step 1: Choose method ──────────────────────────────────────────

  const selectTotp = async () => {
    setMethod('totp');
    setError('');
    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/mfa/totp/setup`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        setQrCode(data.qrCode);
        setTotpSecret(data.secret);
        setChallengeId(data.challengeId);
        setStep(STEPS.SETUP);
      } else {
        setError(data.error || 'Failed to start TOTP setup');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    }
    setIsSubmitting(false);
  };

  const selectWebauthn = () => {
    setMethod('webauthn');
    setError('');
    setStep(STEPS.SETUP);
  };

  // ── Step 2: Setup ─────────────────────────────────────────────────

  const verifyTotp = async (e) => {
    e.preventDefault();
    if (isSubmitting || totpCode.length !== 6) return;
    setIsSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/mfa/totp/verify-setup`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code: totpCode, challengeId }),
      });
      const data = await res.json();
      if (res.ok) {
        setRecoveryCodes(data.recoveryCodes || []);
        setStep(STEPS.RECOVERY);
      } else {
        setError(data.error || 'Invalid code. Please try again.');
        setTotpCode('');
      }
    } catch (err) {
      setError('Verification failed. Please try again.');
    }
    setIsSubmitting(false);
  };

  const registerWebauthn = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      // Get registration options
      const optionsRes = await fetch(`${API_URL}/mfa/webauthn/register-options`, {
        method: 'POST',
        headers,
      });
      const optionsData = await optionsRes.json();
      if (!optionsRes.ok) {
        setError(optionsData.error || 'Failed to get registration options');
        setIsSubmitting(false);
        return;
      }

      // Trigger browser WebAuthn API
      const credential = await startRegistration({ optionsJSON: optionsData.options });

      // Verify with server
      const verifyRes = await fetch(`${API_URL}/mfa/webauthn/register-verify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          challengeId: optionsData.challengeId,
          credential,
          friendlyName: 'Windows Hello',
        }),
      });
      const verifyData = await verifyRes.json();
      if (verifyRes.ok) {
        setRecoveryCodes(verifyData.recoveryCodes || []);
        if (verifyData.recoveryCodes) {
          setStep(STEPS.RECOVERY);
        } else {
          setStep(STEPS.DONE);
        }
      } else {
        setError(verifyData.error || 'Registration failed');
      }
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Registration was cancelled. Please try again.');
      } else {
        setError('WebAuthn registration failed. Please try again.');
      }
    }
    setIsSubmitting(false);
  };

  // ── Recovery codes ────────────────────────────────────────────────

  const copyRecoveryCodes = useCallback(() => {
    const text = recoveryCodes.join('\n');
    navigator.clipboard.writeText(text).then(() => setCopiedCodes(true));
  }, [recoveryCodes]);

  const downloadRecoveryCodes = useCallback(() => {
    const text = `Forwardsflow Recovery Codes\nGenerated: ${new Date().toISOString()}\n\n${recoveryCodes.join('\n')}\n\nKeep these codes safe. Each code can only be used once.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'forwardsflow-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }, [recoveryCodes]);

  // ── Skip for now ──────────────────────────────────────────────────

  const handleSkip = () => {
    if (clearMfaSetupRequired) clearMfaSetupRequired();
    if (user) {
      navigate(getDashboardPath(user.role), { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  const handleComplete = () => {
    if (clearMfaSetupRequired) clearMfaSetupRequired();
    if (user) {
      navigate(getDashboardPath(user.role), { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex font-sans text-black antialiased bg-white">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#4D4D4D] relative overflow-hidden border-r border-[#4D4D4D]">
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <Logo variant="white" />
          </div>
          <div className="space-y-6 my-auto">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-300">Account Security</span>
            <h1 className="text-3xl lg:text-4xl font-bold font-sans text-white leading-tight">
              Set up two-factor authentication to protect your account.
            </h1>
            <p className="text-sm font-mono text-zinc-300 leading-relaxed">
              Two-factor authentication adds an extra layer of security by requiring a second form of verification in addition to your password.
            </p>
          </div>
          <div className="pt-6 border-t border-white/20 flex items-center justify-between text-xs font-mono text-zinc-300">
            <span>&copy; 2026 Forwards Flow Inc.</span>
            <span>Secure your account.</span>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 sm:p-12">
        <div className="max-w-md w-full">
          <div className="lg:hidden mb-8">
            <Logo />
          </div>

          {/* Step Indicator */}
          <div className="mb-8">
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-400 block mb-1">
              {step === STEPS.CHOOSE && 'Step 1 of 3'}
              {step === STEPS.SETUP && 'Step 2 of 3'}
              {step === STEPS.RECOVERY && 'Step 3 of 3'}
              {step === STEPS.DONE && 'Complete'}
            </span>
            <h2 className="text-2xl font-bold font-sans text-black">
              {step === STEPS.CHOOSE && 'Choose your method'}
              {step === STEPS.SETUP && (method === 'totp' ? 'Set up authenticator' : 'Register device')}
              {step === STEPS.RECOVERY && 'Save recovery codes'}
              {step === STEPS.DONE && 'Setup complete'}
            </h2>
          </div>

          <div className="border border-zinc-300 p-8 rounded-none bg-white">
            {error && (
              <div className="mb-6 p-4 bg-zinc-50 border border-zinc-300 rounded-none font-mono text-xs text-black">
                {error}
              </div>
            )}

            {/* ── CHOOSE METHOD ──────────────────────────────────── */}
            {step === STEPS.CHOOSE && (
              <div className="space-y-4">
                <button
                  onClick={selectTotp}
                  disabled={isSubmitting}
                  className="w-full p-5 border border-zinc-300 hover:border-[#4D4D4D] transition-colors text-left group disabled:opacity-50"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-zinc-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-zinc-200">
                      <svg className="w-5 h-5 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-black font-medium">Authenticator App</p>
                      <p className="text-xs font-mono text-zinc-500 mt-1">Use Google Authenticator, Authy, or similar app to generate time-based codes</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={selectWebauthn}
                  disabled={isSubmitting}
                  className="w-full p-5 border border-zinc-300 hover:border-[#4D4D4D] transition-colors text-left group disabled:opacity-50"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-zinc-100 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-zinc-200">
                      <svg className="w-5 h-5 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-mono text-xs uppercase tracking-[0.15em] text-black font-medium">Windows Hello / Fingerprint</p>
                      <p className="text-xs font-mono text-zinc-500 mt-1">Use your device's built-in biometric authentication (fingerprint, face, or PIN)</p>
                    </div>
                  </div>
                </button>

                <div className="pt-4 border-t border-zinc-200">
                  <button
                    onClick={handleSkip}
                    className="w-full py-2 font-mono text-xs text-zinc-500 hover:text-black uppercase tracking-wider"
                  >
                    Skip for now
                  </button>
                </div>
              </div>
            )}

            {/* ── TOTP SETUP ─────────────────────────────────────── */}
            {step === STEPS.SETUP && method === 'totp' && (
              <div className="space-y-6">
                <div>
                  <p className="text-xs font-mono text-zinc-500 mb-4">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  {qrCode && (
                    <div className="flex justify-center mb-4">
                      <img src={qrCode} alt="TOTP QR Code" className="w-48 h-48 border border-zinc-200 p-2" />
                    </div>
                  )}
                  <details className="mb-4">
                    <summary className="text-xs font-mono text-zinc-400 cursor-pointer hover:text-zinc-600 uppercase tracking-wider">
                      Can't scan? Enter code manually
                    </summary>
                    <div className="mt-2 p-3 bg-zinc-50 border border-zinc-200">
                      <code className="text-xs font-mono text-black break-all select-all">{totpSecret}</code>
                    </div>
                  </details>
                </div>

                <form onSubmit={verifyTotp} className="space-y-4">
                  <div>
                    <label className="block text-xs font-mono uppercase tracking-[0.15em] text-zinc-500 mb-2">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 border border-zinc-300 rounded-none text-center text-lg font-mono tracking-[0.5em] text-black placeholder:text-zinc-400 bg-transparent focus:outline-none focus:border-[#4D4D4D] transition-colors disabled:bg-zinc-100"
                      placeholder="000000"
                    />
                    <p className="mt-2 text-xs font-mono text-zinc-400">Enter the 6-digit code shown in your app</p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting || totpCode.length !== 6}
                    className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                </form>

                <button
                  onClick={() => { setStep(STEPS.CHOOSE); setError(''); }}
                  className="w-full py-2 font-mono text-xs text-zinc-500 hover:text-black uppercase tracking-wider"
                >
                  Back
                </button>
              </div>
            )}

            {/* ── WEBAUTHN SETUP ──────────────────────────────────── */}
            {step === STEPS.SETUP && method === 'webauthn' && (
              <div className="space-y-6">
                <div className="text-center py-4">
                  <div className="w-16 h-16 mx-auto mb-4 bg-zinc-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                    </svg>
                  </div>
                  <p className="text-sm font-mono text-zinc-600 mb-1">Register your device</p>
                  <p className="text-xs font-mono text-zinc-400">
                    Click below to register your fingerprint, face, or device PIN via Windows Hello
                  </p>
                </div>

                <button
                  onClick={registerWebauthn}
                  disabled={isSubmitting}
                  className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Registering...</span>
                    </>
                  ) : 'Register Fingerprint'}
                </button>

                <button
                  onClick={() => { setStep(STEPS.CHOOSE); setError(''); }}
                  className="w-full py-2 font-mono text-xs text-zinc-500 hover:text-black uppercase tracking-wider"
                >
                  Back
                </button>
              </div>
            )}

            {/* ── RECOVERY CODES ─────────────────────────────────── */}
            {step === STEPS.RECOVERY && (
              <div className="space-y-6">
                <p className="text-xs font-mono text-zinc-500">
                  Save these recovery codes in a safe place. Each code can only be used once to access your account if you lose your authentication device.
                </p>

                <div className="bg-zinc-50 border border-zinc-200 p-4">
                  <div className="grid grid-cols-2 gap-2">
                    {recoveryCodes.map((code, i) => (
                      <div key={i} className="font-mono text-xs text-black py-1 px-2 bg-white border border-zinc-200 text-center">
                        {code}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={copyRecoveryCodes}
                    className="flex-1 py-2.5 px-3 border border-zinc-300 font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black hover:border-zinc-400 transition-colors"
                  >
                    {copiedCodes ? 'Copied!' : 'Copy Codes'}
                  </button>
                  <button
                    onClick={downloadRecoveryCodes}
                    className="flex-1 py-2.5 px-3 border border-zinc-300 font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black hover:border-zinc-400 transition-colors"
                  >
                    Download
                  </button>
                </div>

                <button
                  onClick={() => setStep(STEPS.DONE)}
                  className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all"
                >
                  I've Saved My Codes
                </button>
              </div>
            )}

            {/* ── DONE ───────────────────────────────────────────── */}
            {step === STEPS.DONE && (
              <div className="space-y-6 text-center">
                <div className="w-16 h-16 mx-auto bg-green-50 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-mono text-black font-medium">Two-factor authentication is enabled</p>
                  <p className="text-xs font-mono text-zinc-500 mt-2">
                    Your account is now protected with {method === 'totp' ? 'an authenticator app' : 'Windows Hello'}.
                    You can manage your settings from the dashboard.
                  </p>
                </div>
                <button
                  onClick={handleComplete}
                  className="w-full py-3.5 px-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] rounded-none hover:bg-black transition-all"
                >
                  Continue to Dashboard
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MfaSetupPage;
